import { RequestSlot } from '@/types/Slots'
import { DaycareType } from '@/types/Daycare'
import moment, { Moment } from 'moment-timezone'
import { CalendarEventType, EventCalendar } from '@/types/Calendar'

interface GroupedRequestSlot {
    key: string
    slots: RequestSlot[]
}

export function dataRequestSlots(
    requestSlots: RequestSlot[],
    datesClosingDays: Moment[],
    daycareType: DaycareType
): EventCalendar[] {
    if (daycareType === DaycareType.rpe || daycareType === DaycareType.laep) {
        return formatRPERequestSlots(requestSlots, datesClosingDays)
    } else {
        return formatRequestSlots(requestSlots, datesClosingDays)
    }
}

function formatRPERequestSlots(requestSlots: RequestSlot[], datesClosingDays: Moment[]): EventCalendar[] {
    const groupedSlotsMap: { [key: string]: GroupedRequestSlot } = {}

    requestSlots.forEach((slot: RequestSlot): void => {
        const dateStr: string = moment(slot.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        const startStr: string = moment(slot.start, 'HH:mm:ss').format('HH:mm')
        const endStr: string = moment(slot.end, 'HH:mm:ss').format('HH:mm')
        const key: string = `${dateStr}-${startStr}-${endStr}-${slot.parentId}`

        if (!groupedSlotsMap[key]) {
            groupedSlotsMap[key] = { key, slots: [] }
        }
        groupedSlotsMap[key].slots.push(slot)
    })

    return Object.values(groupedSlotsMap)
        .filter((group: GroupedRequestSlot) => {
            const { slots } = group
            const firstSlot: RequestSlot = slots[0]

            return !datesClosingDays.find((date: Moment) => date.isSame(moment(firstSlot.date, 'DD-MM-YYYY')))
        })
        .map((group: GroupedRequestSlot) => {
            const { slots } = group
            const firstSlot: RequestSlot = slots[0]

            const start: string = moment(firstSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(firstSlot.end, 'HH:mm:ss').format('HH:mm')

            return {
                title: `${start} - ${end} - ${firstSlot.parentName}`,
                start: moment(
                    moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + start,
                    'YYYY-MM-DD HH:mm'
                ).toDate(),
                end: moment(
                    moment(firstSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + end,
                    'YYYY-MM-DD HH:mm'
                ).toDate(),
                number: slots.length > 1 ? `${slots.length}` : '',
                type: CalendarEventType.REQUEST,
                slots: slots
            }
        })
}

function formatRequestSlots(requestSlots: RequestSlot[], datesClosingDays: Moment[]): EventCalendar[] {
    return requestSlots
        .filter(
            (requestSlot: RequestSlot) =>
                !datesClosingDays.find((date: Moment) => date.isSame(moment(requestSlot.date, 'DD-MM-YYYY')))
        )
        .map((requestSlot: RequestSlot) => {
            const start: string = moment(requestSlot.start, 'HH:mm:ss').format('HH:mm')
            const end: string = moment(requestSlot.end, 'HH:mm:ss').format('HH:mm')

            return {
                title: `${start} - ${end} - ${requestSlot.childName} 
					${requestSlot.childComplement ? ' - ' + requestSlot.childComplement : ''}
					${requestSlot.sectionName ? ' - ' + requestSlot.sectionName : ''}`,
                start: moment(
                    moment(requestSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + start,
                    'YYYY-MM-DD HH:mm'
                ).toDate(),
                end: moment(
                    moment(requestSlot.date, 'DD-MM-YYYY').format('YYYY-MM-DD') + ' ' + end,
                    'YYYY-MM-DD HH:mm'
                ).toDate(),
                number: '',
                type: CalendarEventType.REQUEST,
                slots: [requestSlot]
            }
        })
}
